import type {
  MetadataCreateInputMap,
  MetadataDtoMap,
  MetadataUpdateInputMap,
  NeedDto,
  NeedsActionDto,
  NeedsMetadataDto,
  NeedsResourceDetailsDto,
  NeedViewDto,
} from '@act/shared/data-transfer-objects';
import type {
  ConsolidateDuplicateNeedsRequest,
  CreateResourceDetailsRequest,
  NeedAddActionRequest,
  NeedCreationRequest,
  NeedPatchRequest,
  TransferNeedRequest,
  UpdateResourceDetailsRequest,
} from '@act/shared/types';
import { EndpointDefinition } from '../utils/endpointDefinition';

export const needRequests = {
  deleteAction: EndpointDefinition.delete<
    NeedsActionDto,
    { needId: number; actionId: number }
  >({
    name: 'deleteAction',
    route: '/needs/:needId/action/:actionId',
  }),
  getNeedsForMember: EndpointDefinition.get<NeedViewDto, { memberId: string }>({
    name: 'getNeedsForMember',
    route: '/needs',
  }),

  patchNeed: EndpointDefinition.patch<
    NeedDto,
    NeedPatchRequest,
    { needId: number }
  >({
    name: 'patchNeed',
    route: '/needs/:needId',
  }),

  createNeed: EndpointDefinition.post<NeedDto, NeedCreationRequest, null>({
    name: 'createNeed',
    route: '/needs',
  }),

  addActionToNeed: EndpointDefinition.post<
    NeedsActionDto,
    NeedAddActionRequest,
    { needId: number }
  >({
    name: 'addActionToNeed',
    route: '/needs/:needId/action',
  }),

  createResourceReferral: EndpointDefinition.post<
    NeedsResourceDetailsDto,
    CreateResourceDetailsRequest,
    { actionId: number }
  >({
    name: 'createResourceReferral',
    route: '/needs/:actionId/referral',
  }),

  updateResourceReferral: EndpointDefinition.post<
    NeedsResourceDetailsDto,
    UpdateResourceDetailsRequest,
    { actionId: number }
  >({
    name: 'updateResourceReferral',
    route: '/needs/:actionId/referral',
  }),

  deleteNeed: EndpointDefinition.delete<NeedDto, { needId: number }>({
    name: 'deleteNeed',
    route: '/needs/:needId',
  }),

  reopenNeed: EndpointDefinition.post<
    NeedDto,
    Record<string, never>,
    { needId: number }
  >({
    name: 'reopenNeed',
    route: '/needs/:needId/reopen',
  }),

  getNeedsMetadata: EndpointDefinition.get<NeedsMetadataDto, null>({
    name: 'getNeedsMetadata',
    route: '/needs/metadata',
  }),

  createNeedsMetadata: EndpointDefinition.post<
    MetadataDtoMap[keyof MetadataDtoMap],
    MetadataCreateInputMap[keyof MetadataCreateInputMap],
    { type: keyof MetadataCreateInputMap }
  >({
    name: 'createNeedsMetadata',
    route: '/needs/metadata/:type',
  }),

  updateNeedsMetadata: EndpointDefinition.patch<
    MetadataDtoMap[keyof MetadataDtoMap],
    MetadataUpdateInputMap[keyof MetadataUpdateInputMap],
    { type: keyof MetadataDtoMap; id: number }
  >({
    name: 'updateNeedsMetadata',
    route: '/needs/metadata/:type/:id',
  }),
  getExpiringAndNeedsActionUserNeedsByGuide: EndpointDefinition.get<
    NeedDto[],
    { guideId: string }
  >({
    name: 'getExpiringAndNeedsActionUserNeedsByGuide',
    route: '/needs/expiring-and-needs-action-user-needs-by-guide/:guideId',
  }),

  transferNeedToMember: EndpointDefinition.post<
    NeedDto,
    TransferNeedRequest,
    null
  >({
    name: 'transferNeedToMember',
    route: '/needs/administration/transfer',
  }),

  deleteAccidentallyCreatedAction: EndpointDefinition.delete<
    { success: boolean },
    { needId: number; actionId: number }
  >({
    name: 'deleteAccidentallyCreatedAction',
    route: '/needs/:needId/action/:actionId',
  }),

  consolidateDuplicateNeeds: EndpointDefinition.post<
    { consolidatedNeed: NeedDto; deletedNeedIds: number[]; needsToBroadcast: number[] },
    ConsolidateDuplicateNeedsRequest,
    null
  >({
    name: 'consolidateDuplicateNeeds',
    route: '/needs/administration/consolidate',
  }),
};
